import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { PlateChip } from '../components/PlateChip';
import { api, errMsg, fileUrl } from '../lib/api';
import { peso, manilaDateTime } from '../lib/format';

// Public estimate page — no login, mobile-first, trustworthy
export default function PublicEstimatePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/public/estimate/${token}`);
      setData(d);
    } catch {
      setError(true);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const approve = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/public/estimate/${token}/approve`, { approver_name: name.trim() });
      toast.success('Estimate approved. Thank you!');
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-4">
        <div className="max-w-sm text-center space-y-2">
          <p className="font-medium">This estimate is no longer active.</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Please contact the shop for an updated estimate.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-dvh px-4 py-8 max-w-[720px] mx-auto"><Skeleton className="h-96 rounded-2xl" /></div>;
  }

  if (data.state === 'inactive') {
    return (
      <div className="min-h-dvh flex items-center justify-center px-4 bg-[hsl(var(--background))]">
        <div data-testid="public-estimate-inactive" className="max-w-sm w-full bg-white rounded-2xl border border-black/5 shadow-[var(--shadow-soft)] p-6 text-center space-y-3">
          <p className="font-medium">This estimate is no longer active.</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Please contact {data.shop.name}{data.shop.mobile ? ` at ${data.shop.mobile}` : ''}.</p>
          {data.shop.mobile && (
            <a href={`tel:${data.shop.mobile}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))]">
              <PhoneCall className="h-4 w-4" /> {data.shop.mobile}
            </a>
          )}
        </div>
      </div>
    );
  }

  const approved = data.state === 'approved';

  return (
    <div className="min-h-dvh bg-[hsl(var(--background))] px-4 py-6">
      <div className="max-w-[720px] mx-auto space-y-4">
        <div className="bg-white rounded-2xl border border-black/5 shadow-[var(--shadow-soft)] p-4 sm:p-6 space-y-4">
          {/* Shop header */}
          <div className="flex items-center gap-3 border-b border-[hsl(var(--border))] pb-4">
            {data.shop.logo_url && <img src={fileUrl(data.shop.logo_url)} alt="logo" className="h-12 w-12 rounded-xl object-cover border border-black/5" />}
            <div>
              <h1 className="font-semibold font-display">{data.shop.name}</h1>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{data.shop.address}{data.shop.mobile ? ` · ${data.shop.mobile}` : ''}</p>
            </div>
          </div>

          <p className="text-sm">Hi {data.customer_first_name}! Here is the estimate for your vehicle:</p>

          <div className="rounded-xl bg-[hsl(var(--muted))] p-3 space-y-1.5 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{data.vehicle?.year} {data.vehicle?.make} {data.vehicle?.model}</span>
              <PlateChip plate={data.vehicle?.plate} />
            </div>
            <p className="text-[hsl(var(--muted-foreground))]">Your concern: “{data.complaint}”</p>
          </div>

          {/* Line items */}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="py-2">Item</th>
                <th className="py-2 w-10">Qty</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((it) => (
                <tr key={it.id} className="border-b border-black/5">
                  <td className="py-2 pr-2">
                    <span className={`mr-1.5 text-[10px] uppercase font-medium rounded px-1 py-0.5 ${it.type === 'labor' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-800'}`}>{it.type}</span>
                    {it.description}
                  </td>
                  <td className="py-2 tabular-nums">{it.qty}</td>
                  <td className="py-2 text-right tabular-nums">{peso(it.amount_centavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between rounded-xl border border-black/10 p-3">
            <span className="text-sm font-medium uppercase tracking-wide">Total</span>
            <span data-testid="public-estimate-total" className="text-lg font-semibold tabular-nums">{peso(data.total_centavos)}</span>
          </div>

          {approved ? (
            <div data-testid="public-estimate-approved" className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center space-y-1">
              <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-600" />
              <p className="font-medium text-emerald-800">Approved by {data.approved_by}</p>
              <p className="text-xs text-emerald-700">{manilaDateTime(data.approved_at)}</p>
            </div>
          ) : (
            <form onSubmit={approve} className="space-y-3 border-t border-[hsl(var(--border))] pt-4">
              <p className="text-sm">Type your full name to approve this estimate. Your approval is recorded with date and time.</p>
              <Input
                data-testid="estimate-approve-name-input"
                required
                minLength={2}
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl text-base"
              />
              <Button data-testid="estimate-approve-submit-button" type="submit" disabled={busy || name.trim().length < 2} className="w-full h-12 rounded-xl text-base">
                {busy ? 'Sending…' : 'Approve estimate'}
              </Button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-[hsl(var(--muted-foreground))]">Powered by {data.shop.name}</p>
      </div>
    </div>
  );
}
